import { useEffect } from "react";
import type { JSX } from "react";
import type { RouteObject } from "react-router";
import { createBrowserRouter, RouterProvider, Navigate, useLocation } from "react-router";
import { localDateInZone, mondayOf } from "@recipevault/shared";
import { Library } from "../features/recipes/Library.js";
import { RecipeView } from "../features/recipes/RecipeView.js";
import { RecipeEditor } from "../features/recipes/RecipeEditor.js";
import { Login } from "../features/auth/Login.js";
import { AcceptInvite } from "../features/invites/AcceptInvite.js";
import { HouseholdSettings } from "../features/households/HouseholdSettings.js";
import { Preferences } from "../features/self/Preferences.js";
import { Taxonomy } from "../routes/Taxonomy.js";
import { ImportsIndex } from "../routes/imports/Index.js";
import { NewImport } from "../routes/imports/New.js";
import { PhotoCapture } from "../routes/imports/PhotoCapture.js";
import { PhotoReview } from "../routes/imports/PhotoReview.js";
import { ImportDetail } from "../routes/imports/Detail.js";
import { ShareTarget } from "../routes/ShareTarget.js";
import { NewDispatch } from "../routes/recipes/NewDispatch.js";
import { SessionSummary } from "../features/cook-sessions/SessionSummary.js";
import { CookingMode } from "../features/cook-sessions/CookingMode.js";
import { CookLogHistory } from "../features/cook-sessions/CookLogHistory.js";
import { ShoppingListsIndex } from "../features/shopping-lists/ShoppingListsIndex.js";
import { ShoppingListDetail } from "../features/shopping-lists/ShoppingListDetail.js";
import { PlanWeek } from "../features/meal-plans/PlanWeek.js";
import { Tonight } from "../features/tonight/Tonight.js";
import { AppShell } from "./shell/AppShell.js";
import { RouteErrorScreen } from "./RouteErrorScreen.js";
import { useAuth } from "../features/auth/AuthProvider.js";
import { RESUME_KEY } from "../features/auth/resumeRedirect.js";
import { useHouseholdTimezone } from "../features/auth/useHouseholdTimezone.js";
import { SyncProvider } from "../sync/useSync.js";

// Exported (not just used internally) so tests can drive it directly against
// an arbitrary path, including deep links (e.g. the Task-8 `/share` share
// target) that aren't wired into the app's route table yet.
export function RequireAuth({ children }: { children: JSX.Element }): JSX.Element {
  const { status } = useAuth();
  const location = useLocation();
  const from = location.pathname + location.search;
  // Stash the attempted deep link so Login can resume to it post-sign-in —
  // sessionStorage survives a reload mid-login, unlike Navigate's `state`
  // alone. Hooks must run unconditionally, so this fires every render but
  // only writes on the anonymous branch.
  useEffect(() => {
    if (status === "anonymous") {
      sessionStorage.setItem(RESUME_KEY, from);
    }
  }, [status, from]);
  if (status === "loading") {
    return (
      <main className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading…
      </main>
    );
  }
  if (status === "anonymous") return <Navigate to="/login" state={{ from }} replace />;
  return children;
}

function RecipeViewPage(): JSX.Element {
  const { me } = useAuth();
  const householdId =
    me?.user.currentHouseholdId ?? me?.memberships[0]?.householdId ?? "";
  return <RecipeView householdId={householdId} />;
}

/** `/plan` -> `/plan/<Monday of this week, in the household's timezone>`. */
export function PlanRedirect(): JSX.Element {
  const tz = useHouseholdTimezone();
  const weekStart = mondayOf(localDateInZone(tz, new Date()));
  return <Navigate to={`/plan/${weekStart}`} replace />;
}

// The `RouteObject[]` is exported (not just the built router) so tests can
// inspect route registration directly, and so later plan tasks can extend it
// without threading a new export through createBrowserRouter each time.
export const routes: RouteObject[] = [
  { path: "/login", element: <Login />, errorElement: <RouteErrorScreen /> },
  {
    path: "/accept-invite",
    element: <AcceptInvite />,
    errorElement: <RouteErrorScreen />,
  },
  {
    // Legacy path form — kept for in-flight invites sent before the ?token= switch
    // (invites live ≤ 7 days, so this can be removed after 2026-07-16).
    path: "/invites/:token/accept",
    element: <AcceptInvite />,
    errorElement: <RouteErrorScreen />,
  },
  {
    // Day-first home (Plan 12 Task 5) — the Tonight surface.
    path: "/",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <Tonight />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/recipes",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <Library />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/settings/household",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <HouseholdSettings />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/settings/preferences",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <Preferences />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/settings/taxonomy",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <Taxonomy />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/recipes/new",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <NewDispatch />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/recipes/new/draft",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <RecipeEditor />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/imports",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <ImportsIndex />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/imports/new",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <NewImport />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/imports/photo",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <PhotoCapture />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/imports/:id/review",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <PhotoReview />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/imports/:id",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <ImportDetail />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    // Android Web Share Target landing (manifest `share_target`, Plan 12 Task 8).
    path: "/share",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <ShareTarget />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    // Fullscreen cooking screen — no AppShell wrapper.
    path: "/cook/:sessionId",
    element: (
      <RequireAuth>
        <SyncProvider>
          <CookingMode />
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    // Fullscreen post-cook summary — no AppShell wrapper.
    path: "/cook/:sessionId/summary",
    element: (
      <RequireAuth>
        <SyncProvider>
          <SessionSummary />
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/recipes/:id",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <RecipeViewPage />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/recipes/:id/edit",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <RecipeEditor />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/recipes/:id/cook-log",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <CookLogHistory />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/shopping",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <ShoppingListsIndex />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/shopping/:id",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <ShoppingListDetail />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/plan",
    element: (
      <RequireAuth>
        <PlanRedirect />
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
  {
    path: "/plan/:weekStart",
    element: (
      <RequireAuth>
        <SyncProvider>
          <AppShell>
            <PlanWeek />
          </AppShell>
        </SyncProvider>
      </RequireAuth>
    ),
    errorElement: <RouteErrorScreen />,
  },
];

const router = createBrowserRouter(routes);

export function AppRouter(): JSX.Element {
  return <RouterProvider router={router} />;
}
